import { APIClient, getLoggedinUser } from "../helpers/api_helper";
import * as url from "../helpers/url_helper";
import type { PaginatedResponse } from "../types/api";

const api = new APIClient();

// Check if we should use the fake backend
// Helper function to get tenantId from logged-in user
const getTenantId = (): string | null => {
    const authUser = getLoggedinUser();
    return authUser?.tenantId || null;
};

// Enums
export enum ProductCategory {
    Frame = 1,
    Cuplock = 2,
    Ladder = 3,
    Accessory = 4,
    Platform = 5,
    SafetyAndAccess = 6
}

export const ProductCategoryLabels: Record<ProductCategory, string> = {
    [ProductCategory.Frame]: "Frame",
    [ProductCategory.Cuplock]: "Cuplock",
    [ProductCategory.Ladder]: "Ladder",
    [ProductCategory.Accessory]: "Accessory",
    [ProductCategory.Platform]: "Platform",
    [ProductCategory.SafetyAndAccess]: "Safety & Access"
};

/**
 * Product Category Style Map - LOCKED DESIGN SYSTEM
 * 
 * DO NOT MODIFY: This is a fixed design system mapping.
 * Rules:
 * - Only icons are colored, labels use default text color
 * - No background colors on dropdown items
 * - Use this mapping everywhere (dropdown, list, detail pages)
 */
export interface ProductCategoryStyle {
    icon: string;
    color: string;
}

export const ProductCategoryStyleMap: Record<ProductCategory, ProductCategoryStyle> = {
    [ProductCategory.Frame]: {
        icon: "ri-layout-grid-line", // LayoutGrid
        color: "#475569", // text-slate-600
    },
    [ProductCategory.Cuplock]: {
        icon: "ri-links-line", // Link2
        color: "#4F46E5", // text-indigo-600
    },
    [ProductCategory.Ladder]: {
        icon: "ri-barricade-line", // Ladder (closest RemixIcon equivalent)
        color: "#F59E0B", // text-amber-500
    },
    [ProductCategory.Accessory]: {
        icon: "ri-tools-line", // Wrench
        color: "#6B7280", // text-gray-500
    },
    [ProductCategory.Platform]: {
        icon: "ri-layout-line", // RectangleHorizontal
        color: "#16A34A", // text-green-600
    },
    [ProductCategory.SafetyAndAccess]: {
        icon: "ri-shield-check-line", // ShieldCheck
        color: "#DC2626", // text-red-600
    },
};

// Legacy exports for backward compatibility (deprecated - use ProductCategoryStyleMap instead)
export const ProductCategoryIcons: Record<ProductCategory, string> = {
    [ProductCategory.Frame]: ProductCategoryStyleMap[ProductCategory.Frame].icon,
    [ProductCategory.Cuplock]: ProductCategoryStyleMap[ProductCategory.Cuplock].icon,
    [ProductCategory.Ladder]: ProductCategoryStyleMap[ProductCategory.Ladder].icon,
    [ProductCategory.Accessory]: ProductCategoryStyleMap[ProductCategory.Accessory].icon,
    [ProductCategory.Platform]: ProductCategoryStyleMap[ProductCategory.Platform].icon,
    [ProductCategory.SafetyAndAccess]: ProductCategoryStyleMap[ProductCategory.SafetyAndAccess].icon,
};

export enum RentalRateType {
    PerDay = 1,
    PerWeek = 2,
    PerMonth = 3
}

export const RentalRateTypeLabels: Record<RentalRateType, string> = {
    [RentalRateType.PerDay]: "Per Day",
    [RentalRateType.PerWeek]: "Per Week",
    [RentalRateType.PerMonth]: "Per Month"
};

// Interface for API Response & Frontend Use
export interface ProductItem {
    id: string;
    tenantId: string;
    tenantName?: string;
    productName: string;
    productCode: string;
    description: string;
    productCategory: ProductCategory;
    defaultRentalRate: number;
    rentalRateType: RentalRateType;
    specifications: string;
    isActive: boolean;
    isDeleted: boolean;
    created?: string;
    modified?: string;
}

// Interface for API Requests (POST/PUT)
export interface ProductPayload {
    tenantId?: string; // Optional in PUT, required in POST (if not handled by backend)
    productName: string;
    productCode: string;
    description: string;
    productCategory: ProductCategory;
    defaultRentalRate: number;
    rentalRateType: RentalRateType;
    specifications: string;
    isActive: boolean;
}

// Service Methods
export const getProducts = (
    pageNumber: number = 1,
    pageSize: number = 200, // Updated default to match requirement
    tenantId?: string | null
): Promise<PaginatedResponse<ProductItem>> => {
    
    
    // Get tenantId if not provided
    const finalTenantId = tenantId || getTenantId();
    
    // Real API call with query parameters including tenantId and isActive=true
    const queryParams: Record<string, string | number | boolean | undefined> = {
        pageNumber,
        pageSize,
        isActive: true // Ensure only active products are fetched as per requirement
    };
    
    if (finalTenantId) {
        queryParams.tenantId = finalTenantId;
    }
    
    return api.get<PaginatedResponse<ProductItem>>(url.GET_PRODUCTS, queryParams);
};

export const getProductById = (id: string, tenantId?: string | null): Promise<ProductItem> => {
    
    
    // Get tenantId if not provided
    const finalTenantId = tenantId || getTenantId();
    
    // Real API call with query parameters including tenantId
    const queryParams: Record<string, string | number | boolean | undefined> = {};
    
    if (finalTenantId) {
        queryParams.tenantId = finalTenantId;
    }
    
    return api.get<ProductItem>(`${url.GET_PRODUCTS}/${id}`, queryParams);
};

export const addNewProduct = (product: ProductPayload): Promise<ProductItem> => {
    
    
    // Get tenantId if not provided in product data
    const tenantId = product.tenantId || getTenantId();
    
    if (!tenantId) {
        return Promise.reject(new Error("Organization ID is required"));
    }
    
    // Format payload according to API specification
    // API expects 'unitPrice' instead of 'defaultRentalRate'
    const payload: any = {
        tenantId: tenantId,
        productName: product.productName,
        productCode: product.productCode,
        description: product.description,
        productCategory: product.productCategory,
        unitPrice: product.defaultRentalRate, // Map defaultRentalRate to unitPrice for API
        rentalRateType: product.rentalRateType,
        specifications: product.specifications,
        isActive: product.isActive !== undefined ? product.isActive : true,
    };
    
    return api.create<ProductItem>(url.ADD_NEW_PRODUCT, payload);
};

export const updateProduct = (id: string, product: ProductPayload): Promise<ProductItem> => {
    
    
    // Get tenantId if not provided in product data
    const tenantId = product.tenantId || getTenantId();
    
    if (!tenantId) {
        return Promise.reject(new Error("Organization ID is required"));
    }
    
    // Format payload according to API specification
    // API expects 'unitPrice' instead of 'defaultRentalRate'
    const payload: Record<string, unknown> = {
        tenantId: tenantId,
        productName: product.productName,
        productCode: product.productCode,
        description: product.description,
        productCategory: product.productCategory,
        unitPrice: product.defaultRentalRate, // Map defaultRentalRate to unitPrice for API
        rentalRateType: product.rentalRateType,
        specifications: product.specifications,
        isActive: product.isActive !== undefined ? product.isActive : true,
    };
    
    return api.put<ProductItem>(`${url.UPDATE_PRODUCT}/${id}`, payload);
};

export const deleteProduct = (id: string): Promise<void> => {
    
    
    return api.delete<void>(`${url.DELETE_PRODUCT}/${id}`);
};
